/// <amd-module name="Wiki/Renderer/LinkTransformer" />
import { format, startsWith } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { StateMergeOptions } from "VSS/Platform/History";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

import { isAbsoluteUrl, isExternalUrl, isSafeProtocol } from "VSS/Features/Markdown/Utils/UrlHelper";

import { GitVersionDescriptor } from "Wiki/Clients/VC/Contracts/Git";
import { WikiPage, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { getExternalWikiHubPageUrl, isInternalAnchorLink } from "Wiki/Renderer/Helpers";
import * as VCPathHelper from "Wiki/Renderer/VCPathHelper";
import { getGitFileContentUrl } from "Wiki/Renderer/VCUrlHelper";
import { FormatConstants, PathConstants, RepoConstants, TemplateConstants, UrlConstants, UrlEscapeConstants } from "Wiki/ViewCommon/Constants";
import { UrlParameters } from "Wiki/ViewCommon/Contracts";
import { getGitItemPath, removeMdExtensionFromPagePath } from "Wiki/ViewCommon/Utils/PathHelper";
import { getPagePathBasedUrlAndState, getVersionFromBranchName } from "Wiki/ViewCommon/Utils/WikiHelper";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export class LinkTransformer {
    constructor(
        private _pageContext: IVssPageContext,
        private _projectId: string,
        private _repositoryId: string,
        private _wikiMappedPath: string,
        private _isHostedOutsideWikiHub?: boolean,
        private _wikiIdentifier?: string,
        private _wikiVersion?: GitVersionDescriptor
    ) {}

    /**
     * Function to transform links in Markdown page renderer
     * @param {string} href - link to be transformed
     * @returns transformed link
     */
    public transformLink(href: string, currentPagePath: string = "", getWikiPage?: (pagePath: string) => WikiPage | undefined): string {
        if (isAbsoluteUrl(href) || isSafeProtocol(href) || isInternalAnchorLink(href)) {
            return href;
        }

        const linkParts: string[] = href.split("#");
        let pathInLink: string = linkParts[0];
        const anchorInLink: string = linkParts[1];

        const absoluteCurrentPagePath = removeMdExtensionFromPagePath(getGitItemPath(currentPagePath, this._wikiMappedPath));
        const wikiRootDirectory =
            this._wikiMappedPath === RepoConstants.RootPath ? this._wikiMappedPath : this._wikiMappedPath + RepoConstants.RootPath;
        // We consider paths starting with "/" and ".attachments" both as absolute paths, else relative
        const linkBasePath =
            startsWith(pathInLink, RepoConstants.RootPath) || startsWith(pathInLink, RepoConstants.AttachmentsFolder.substr(1))
                ? wikiRootDirectory
                : absoluteCurrentPagePath;

        /*
         * 1. Here we handle links which are downloaded
         * Eg.
         * - /WikiMappedPath/.attachments/image.docx
         * - /WikiMappedPath/.attachments/README.md
         * - /SomePathOutsideWiki/README.md
         * - /SomePathOutsideWiki/README
         */
        const attachmentRelativePath = pathInLink && pathInLink.charAt(0) === RepoConstants.RootPath ? pathInLink.substr(1) : pathInLink;
        const attachmentAbsolutePath = VCPathHelper.combine(linkBasePath, attachmentRelativePath);
        const fileExtension = VCPathHelper.getFileExtension(attachmentAbsolutePath);
        const formattedFileType = format(".{0}", fileExtension);
        const isMDFileAttachment = formattedFileType && formattedFileType.toLowerCase() === FormatConstants.MDFormat;
        const isLinkOutsideWiki = !startsWith(attachmentAbsolutePath, wikiRootDirectory);

        if (isMDFileAttachment) {
            /*
             * MD file links are treated specially.
             * 1. MD link for a page in the attachments folder or templates folder should be downloaded
             * 2. MD link for a page outside the Wiki should be downloaded
             * 3. MD link for a page inside the Wiki should be navigated to
             */

            const isMDFileInsideReservedFolders =
                _isPathInGivenFolder(attachmentAbsolutePath, combineUrlPaths(this._wikiMappedPath, RepoConstants.AttachmentsFolder)) ||
                _isPathInGivenFolder(attachmentAbsolutePath, combineUrlPaths(this._wikiMappedPath, TemplateConstants.TemplateDirectory));
            const isMDFileOutsideWiki = isLinkOutsideWiki;

            if (isMDFileInsideReservedFolders || isMDFileOutsideWiki) {
                return _getAttachmentFileContentUrl(
                    this._pageContext,
                    this._projectId,
                    this._repositoryId,
                    attachmentAbsolutePath,
                    this._wikiVersion
                );
            }
        } else if (_isWikiAttachment(this._pageContext, attachmentAbsolutePath, this._wikiMappedPath, true)) {
            // We handle other attachments here
            return _getAttachmentFileContentUrl(this._pageContext, this._projectId, this._repositoryId, attachmentAbsolutePath, this._wikiVersion);
        } else if (!fileExtension && isLinkOutsideWiki) {
            // We assume links without extensions to point to MD pages only. Eg /parentPage/ChildPage
            // So, if we have such a link pointing to outside wiki, we try to download it
            return _getAttachmentFileContentUrl(
                this._pageContext,
                this._projectId,
                this._repositoryId,
                attachmentAbsolutePath + FormatConstants.MDFormat,
                this._wikiVersion
            );
        }

        /*
         * 2. Here we handle links pointing to pages inside the wiki, to which we can navigate
         * Eg.
         * - /ParentPage/ChildPage
         * - /ParentPage/ChildPage.md
         */

        // Make links case insensitive, if there is a page with same name, but different case, link should point to that page

        if (getWikiPage) {
            const wikiPagePath = getWikiPage(pathInLink.toLocaleLowerCase());

            if (wikiPagePath) {
                pathInLink = wikiPagePath.path;
            }
        }

        // Update URL parameters 'pagePath' and 'anchor'
        const urlParams: UrlParameters = {};
        const relativePagePath = pathInLink && pathInLink.charAt(0) === RepoConstants.RootPath ? pathInLink.substr(1) : pathInLink;

        let absolutePagePath = VCPathHelper.combine(linkBasePath, relativePagePath);
        // Getting absolute path of the page inside the Wiki
        absolutePagePath = this._wikiMappedPath !== RepoConstants.RootPath ? absolutePagePath.substr(this._wikiMappedPath.length) : absolutePagePath;
        // Removing extension
        absolutePagePath = removeMdExtensionFromPagePath(absolutePagePath);

        // Decode page path from the link since we encode certain characters for supporting those in page names
        let decodedPagepath: string;
        const escapedWikiLink = _getEscapedInternalWikiLink(absolutePagePath);
        try {
            decodedPagepath = decodeURIComponent(escapedWikiLink);
        } catch (e) {
            decodedPagepath = escapedWikiLink;
        }

        urlParams.pagePath = decodedPagepath;

        if (anchorInLink && _isDeepAnchorLink(href)) {
            try {
                urlParams.anchor = decodeURIComponent(anchorInLink.toLowerCase());
            } catch (e) {
                urlParams.anchor = anchorInLink.toLowerCase();
            }
        }

        if (this._isHostedOutsideWikiHub) {
            urlParams.wikiIdentifier = this._wikiIdentifier;
            return getExternalWikiHubPageUrl(this._pageContext, urlParams);
        } else {
            return getPagePathBasedUrlAndState(this._pageContext, urlParams, {
                wikiIdentifier: this._wikiIdentifier,
                wikiVersion: this._wikiVersion && getVersionFromBranchName(this._wikiVersion.version)
            }).url;
        }
    }
}

// Exported for testing
export function _getAttachmentFileContentUrl(
    pageContext: IVssPageContext,
    projectId: string,
    repositoryId: string,
    attachmentAbsolutePath: string,
    wikiVersion?: GitVersionDescriptor
): string {
    try {
        attachmentAbsolutePath = decodeURIComponent(attachmentAbsolutePath);
    } catch (e) {
        // attachmentAbsolutePath malformed, do not decode
    }

    let includeContentMetadata: boolean = isFeatureFlagEnabled(pageContext, "Wiki.DetectTextFileCharset", false);

    // Generate file content link for the attachment file
    return getGitFileContentUrl(pageContext, projectId, repositoryId, wikiVersion, attachmentAbsolutePath, true, includeContentMetadata);
}

// Exported for testing
export function _isWikiAttachment(pageContext: IVssPageContext, absolutePath: string, wikiRootPath: string, isLink: boolean = false): boolean {
    if (_isPathInGivenFolder(absolutePath, combineUrlPaths(wikiRootPath, RepoConstants.AttachmentsFolder))) {
        // If path is in attachments folder, return true.
        return true;
    } else if (isLink) {
        const fileType = format(".{0}", VCPathHelper.getFileExtension(absolutePath));
        return !(PathConstants.AllowedAttachmentFileTypes(pageContext).indexOf(fileType.toUpperCase()) === -1);
    }

    return false;
}

// Exported for testing
export function _isPathInGivenFolder(absolutePath: string, folderPath: string): boolean {
    return absolutePath.substr(0, folderPath.length) === folderPath;
}

// Exported for testing
export function _isDeepAnchorLink(path: string): boolean {
    const hashIndex = path.indexOf("#");
    const params = path.split("#");

    // It is deep anchor link (i.e., /PageName#section) if path contains '#'
    return hashIndex > 0 && !!params[0] && !!params[1];
}

// Exported for testing
export function _getEscapedInternalWikiLink(href: string): string {
    if (isExternalUrl(href)) {
        return href;
    }

    // We do this to support links which still have "-" for space instead of "%20"
    return href.replace(UrlEscapeConstants.HyphenRegExp, UrlEscapeConstants.SpaceEncoding);
}
